import { useCallback, useEffect, useState } from 'react'
import { trackEvent } from '../lib/analytics'

const DISMISS_KEY = 'install-prompt-dismissed-at'
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function isIosSafari(): boolean {
  if (typeof window === 'undefined') return false
  const ua = window.navigator.userAgent
  // iPadOS 13+ reports a Mac UA ("Macintosh; ... Mac OS X"), so checking
  // just the iPhone/iPad/iPod tokens misses iPad. The combination
  // `platform === 'MacIntel'` + `maxTouchPoints > 1` is the standard
  // workaround Apple itself recommends.
  const ua_isIos = /iPhone|iPad|iPod/.test(ua)
  const isIpadOS =
    /Macintosh/.test(ua) &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1
  const isIos = ua_isIos || isIpadOS
  // Exclude in-app webviews (Instagram, Facebook, Line, etc.) — they look
  // like Safari but can't install PWAs, so showing them iOS instructions
  // is misleading.
  const isInAppBrowser = /FBAN|FBAV|Instagram|Line|Twitter|WhatsApp/.test(ua)
  const isSafari =
    /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua) && !isInAppBrowser
  return isIos && isSafari
}

// Wrapped in try/catch because Mobile Safari in private mode throws on
// localStorage access (QuotaExceededError / SecurityError). An uncaught
// throw here would bubble through the InstallPrompt's useState initializer
// and crash the whole <Layout> via its error boundary.
function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Swallowed: in private mode we just lose the 14-day dismissal memory.
  }
}

function isDismissed(): boolean {
  const raw = safeGetItem(DISMISS_KEY)
  if (!raw) return false
  const at = Number(raw)
  if (Number.isNaN(at)) return false
  return Date.now() - at < DISMISS_TTL_MS
}

/**
 * Manages the install-prompt lifecycle.
 *
 * Two platforms, two behaviours:
 *   - Android / Chrome / Edge fire `beforeinstallprompt`; we stash the event
 *     and surface `promptInstall()` which calls `event.prompt()` when invoked.
 *   - iOS Safari has no programmatic API — we detect it and let the UI show
 *     manual "Add to Home Screen" instructions instead.
 *
 * Dismissal is sticky for 14 days (localStorage). After that the prompt is
 * eligible to appear again on the assumption the user's situation may have
 * changed.
 */
export function useInstallPrompt(): {
  canInstall: boolean
  isIos: boolean
  dismissed: boolean
  promptInstall: () => Promise<void>
  dismiss: () => void
} {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [dismissed, setDismissed] = useState(() => isDismissed())
  const ios = isIosSafari()

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault()
      setDeferred(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall)
  }, [])

  const promptInstall = useCallback(async () => {
    if (!deferred) return
    await deferred.prompt()
    const choice = await deferred.userChoice
    // Report the native-prompt outcome so GA4 shows the install funnel
    // (prompt accepted vs dismissed). The actual install is also captured
    // separately via the `appinstalled` event (see usePwaInstallAnalytics).
    trackEvent('pwa_install_prompt', { outcome: choice.outcome })
    setDeferred(null)
  }, [deferred])

  const dismiss = useCallback(() => {
    safeSetItem(DISMISS_KEY, String(Date.now()))
    setDismissed(true)
  }, [])

  const canInstall = !dismissed && (deferred !== null || ios)

  return { canInstall, isIos: ios, dismissed, promptInstall, dismiss }
}
